import React from 'react';
import {
  FormControl,
  FormHelperText,
  SxProps,
  Theme,
  useTheme,
  Skeleton,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import FormLabel from '../../../atoms/formLabel/index';
import { StyledInput } from './element';

interface Iprops {
  sxFC?: SxProps<Theme>;
  sxFL?: SxProps<Theme>;
  name: string;
  label?: string;
  errors?: any;
  control?: any;
  defaultValue: Date | null;
  disabled?: boolean;
  onChange?: (newValue: Date | null) => void;
  required?: boolean;
  placeholder?: string;
  views?: Array<string>;
  inputFormat?: string;
  isLoading?: boolean;
  specificName?: string;
}

export default function DateTimePickerForm(props: Iprops) {
  const {
    sxFC,
    sxFL,
    name,
    label,
    control,
    defaultValue,
    errors,
    disabled,
    onChange: onChangeValue,
    required = false,
    placeholder = '',
    views = ['year', 'month', 'day', 'hours', 'minutes', 'seconds'],
    inputFormat = 'dd MMM yyyy HH:mm:ss', // Updated inputFormat
    isLoading = false,
    specificName = '',
  } = props;
  const { palette } = useTheme();
  return (
    <FormControl
      sx={{
        flex: 1,
        display: 'flex',
        mb: '1rem',
        ...sxFC,
      }}
      variant="standard"
    >
      {label?.length !== 0 && (
        <FormLabel htmlFor={name} sx={{ color: palette.neutral.dark, ...sxFL }}>
          {label}
          {!required && !disabled && (
            <span
              style={{
                marginLeft: '4px',
                color: palette.neutral.light,
                fontWeight: 400,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                fontStyle: 'italic',
              }}
            >
              (optional)
            </span>
          )}
        </FormLabel>
      )}
      {!isLoading && (
        <Controller
          defaultValue={defaultValue}
          control={control}
          name={name}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                {...field}
                inputFormat={inputFormat}
                value={field.value || null}
                disabled={disabled}
                onChange={(newValue: Date | null) => {
                  field.onChange(newValue);
                  onChangeValue?.(newValue);
                }}
                disableMaskedInput
                // views={views}
                componentsProps={{
                  actionBar: {
                    actions: ['clear', 'accept'],
                  },
                }}
                onAccept={(newDate) => {
                  console.log(newDate);
                }}
                renderInput={(params: any) => {
                  const newParams = {
                    ...params,
                    inputProps: {
                      ...params.inputProps,
                      placeholder: placeholder || 'dd MMM yyyy HH:mm:ss', // Updated placeholder
                    },
                  };

                  return (
                    <StyledInput
                      id={name}
                      sx={[
                        { fontSize: '1rem', padding: '8px 12px' }, // Adjusted font size and padding
                        errors[name] !== undefined && {
                          '& fieldset': { borderColor: palette.error.main },
                        },
                      ]}
                      helperText={null}
                      size="small"
                      {...newParams}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          )}
        />
      )}
      {isLoading && (
        <Skeleton
          style={{
            height: '40px',
            borderRadius: '4px',
          }}
        />
      )}
      <FormHelperText sx={{ fontSize: '0.75rem', color: palette.error.main }}>
        {errors[name] && errors[name].message}
        {errors[specificName]?.message && errors[specificName].message}
      </FormHelperText>
    </FormControl>
  );
}